import React, { useEffect, useState } from 'react'
import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup'
import { Link } from 'react-router-dom';
import Switch from 'react-switch';
import { stepType } from '../CreateCourse';
import ResourceLoader from '../../../../../../../components/loader/ResourceLoader';
import { createResource } from '../../../../../../../api/uploadResourse';
import toast from 'react-hot-toast';
import { errorResponse } from '../../../../../../../components/utils/errorResponse';
import HorizontalTab from '../../../../../../../components/Tab/HorizontalTab';
import { api as courses } from '../../../../../../../api/courses';
import { useQuery } from 'react-query';
import CustomDropdown from '../../../../../../../components/dropdown/CustomDropdown';

type CourseInformationType = {
  setStep: React.Dispatch<React.SetStateAction<stepType>>
  type?: 'edit' | null,
  courseInfo?: any
}

export type languageType = {
  language: "english" | "french" | "portuguese" | "en" | "fr" | "pt",
  symbol: "en" | "fr" | "pt",
  name: string | undefined,
  description: string | undefined,
  isDefault: boolean,
  filePath?: string,
}

type initialValuesType = {
  title: string,
  description: string,
  category: string,
  tag?: string,
  rating: number,
  code: string,
  status: ("draft" | "published"),
  title_f?: string,
  description_f?: string,
  title_p?: string,
  description_p?: string,
}

const CourseInformation: React.FC<CourseInformationType> = (props): React.ReactElement => {

  const validationSchema = Yup.object<Yup.AnyObject, {}>().shape({
    title: Yup.string().required('Error! field title field is required.'),
    description: Yup.string().required('Error! description field is required.'),
    category: Yup.string().required('Error! category is required.'),
  });

  const { data: categories, isLoading } = useQuery(
    ["get categories"],
    () => {
      return courses.getCategories({ perPage: 10000 });
    },
    {
      cacheTime: 10000,
      staleTime: 10000,
      refetchOnWindowFocus: true,
      select: (data: any) => data?.data,
    }
  );

  // Credit code API intentionally disabled for this flow.
  // const { data: creditsCodes, isLoading: creditsCodesLoading } = useQuery(
  //   ["get credit codes"],
  //   () => {
  //     return courses.getCreditCodes({ perPage: 10000 });
  //   },
  //   {
  //     cacheTime: 10000,
  //     staleTime: 10000,
  //     refetchOnWindowFocus: true,
  //     select: (data: any) => data?.data,
  //   }
  // );

  const { type, courseInfo } = props
  const isEdit = type === 'edit'

  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [logo, setLogo] = useState<string>('')
  const [certificate, setCertificate] = useState<string>('')
  const [filePathEn, setFilePathEn] = useState<string>('')
  const [filePathFr, setFilePathFr] = useState<string>('')
  const [filePathPt, setFilePathPt] = useState<string>('')
  const [useMultipleLanguages, setUseMultipleLanguages] = useState<boolean>(
    isEdit ? courseInfo?.languages?.length > 1 : false
  );
  const [courseLoading, setCourseLoading] = useState<boolean>(false)

  const getLanguageData = (symbol: 'en' | 'fr' | 'pt', key: string): string => {
    return isEdit ?
      courseInfo?.languages?.length > 1 ?
        courseInfo?.languages?.find((language: any) => {
          return language?.symbol === symbol || (symbol === 'pt' && language?.symbol === 'pr')
        })?.[key] || '' : '' : ''
  }

  useEffect(() => {
    if (isEdit) {
      setLogo(courseInfo?.imagePath)
      setCertificate(courseInfo?.certificatePath || '')
      setFilePathEn(courseInfo?.languages?.find((l: any) => l.symbol === 'en')?.filePath || '')
      setFilePathFr(courseInfo?.languages?.find((l: any) => l.symbol === 'fr')?.filePath || '')
      setFilePathPt(courseInfo?.languages?.find((l: any) => l.symbol === 'pt' || l.symbol === 'pr')?.filePath || '')
    }
  }, [courseInfo])

  const initialValues: initialValuesType = {
    title: isEdit ? courseInfo?.name : '',
    description: isEdit ? courseInfo?.description : '',
    title_f: getLanguageData('fr', 'name'),
    description_f: getLanguageData('fr', 'description'),
    title_p: getLanguageData('pt', 'name'),
    description_p: getLanguageData('pt', 'description'),
    category: isEdit ? courseInfo?.categoryId : '',
    tag: '',
    code: isEdit ? courseInfo?.courseCode : '',
    status: isEdit ? courseInfo?.status : 'published',
    rating: 5,
  }

  const uploadCourseImage = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    setIsUploading(true)
    try {
      const file: File = event?.target?.files![0];
      if (file) {
        const formData: FormData = new FormData();
        formData.append("file", file);
        const response = await createResource.uploadResource(formData)
        toast.success(response?.data?.message)
        setLogo(response?.data?.data)
        setIsUploading(false)
      }
    } catch (error) {
      setIsUploading(false)
      errorResponse(error)
    }
  }

  const uploadCertificate = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    setIsUploading(true)
    try {
      const file: File = event?.target?.files![0];
      if (file) {
        const formData: FormData = new FormData();
        formData.append("file", file);
        const response = await createResource.uploadResource(formData)
        toast.success(response?.data?.message)
        setCertificate(response?.data?.data)
        setIsUploading(false)
      }
    } catch (error) {
      setIsUploading(false)
      errorResponse(error)
    }
  }

  const uploadCourseFile = async (event: React.ChangeEvent<HTMLInputElement>, symbol: 'en' | 'fr' | 'pt'): Promise<void> => {
    setIsUploading(true)
    try {
      const file: File = event?.target?.files![0];
      if (file) {
        const formData: FormData = new FormData();
        formData.append("file", file);
        const response = await createResource.uploadResource(formData)
        toast.success(response?.data?.message)
        if (symbol === 'en') setFilePathEn(response?.data?.data)
        if (symbol === 'fr') setFilePathFr(response?.data?.data)
        if (symbol === 'pt') setFilePathPt(response?.data?.data)
        setIsUploading(false)
      }
    } catch (error) {
      setIsUploading(false)
      errorResponse(error)
    }
  }

  const createCourse = async (values: initialValuesType, action: any): Promise<any> => {
    action?.setSubmitting(false)
    if (useMultipleLanguages) {
      if (!values.title_f || !values.title_p || !values.description_f || !values.description_p) {
        toast.error(`Error! please add title and description for all languages.`)
        return
      }
    }
    if (!logo) {
      toast.error('Course Image is Required.')
      return
    }

    const defaultLanguage: languageType = {
      description: values.description, name: values.title,
      language: 'en', symbol: 'en', isDefault: true,
      filePath: filePathEn || undefined
    }

    const languages: languageType[] = useMultipleLanguages ?
      [
        defaultLanguage,
        {
          description: values.description_f, name: values.title_f,
          language: 'fr', symbol: 'fr', isDefault: false,
          filePath: filePathFr || undefined
        },
        {
          description: values.description_p, name: values.title_p,
          language: 'pt', symbol: 'pt', isDefault: false,
          filePath: filePathPt || undefined
        }
      ] :
      [defaultLanguage]

    const payload = {
      categoryId: values.category,
      name: values.title,
      description: values.description,
      points: 30, // Default mock or add to form
      price: 120.5, // Default mock or add to form
      status: values.status,
      duration: "3h 30m", // Default mock
      imagePath: logo,
      certificatePath: certificate || undefined,
      languages
    }
    try {
      toast.loading('Loading...')
      setCourseLoading(true)
      let response: any = null
      if (isEdit) response = await courses.updateCourse(payload, courseInfo?.id)
      else response = await courses.createCourse(payload)
      toast.remove()
      setCourseLoading(false)
      toast.success(response?.message)
      sessionStorage.setItem('active-course', JSON.stringify(response?.data))
      props.setStep(2);
    } catch (error: any) {
      toast.remove()
      setCourseLoading(false)
      toast.error(error?.response?.data?.message)
    }
  }

  const supportedLanguages: ('English' | 'French' | 'Portuguese')[] = ['English', 'French', 'Portuguese']

  const tabsData: { label: string, content: React.ReactElement }[] = supportedLanguages.map(
    (language: string) => {
      const symbol = language === 'English' ? 'en' : language === 'French' ? 'fr' : 'pt';
      const currentFilePath = symbol === 'en' ? filePathEn : symbol === 'fr' ? filePathFr : filePathPt;
      return {
        label: language,
        content: (
          <>
            <section className='grid grid-cols-1 gap-5 mt-1'>
              <div>
                <label
                  htmlFor={language.startsWith('E') ? "title" : `title_${language.toLocaleLowerCase().slice(0, 1)
                    }`}
                  className='text-xs'
                >
                  Course Title {language.startsWith('E') ? "" : `(${language})`}
                  <span className='text-lg text-red-500'>*</span>
                </label>
                <Field
                  id={language.startsWith('E') ? "title" : `title_${language.toLocaleLowerCase().slice(0, 1)
                    }`}
                  className="input-style bg-primaryGray"
                  name={language.startsWith('E') ? "title" : `title_${language.toLocaleLowerCase().slice(0, 1)
                    }`}
                  placeholder='Type course title here...'
                />
                <p className='mt-2 text-red-500 text-sm'>
                  <ErrorMessage
                    name={language.startsWith('E') ? "title" : `title_${language.toLocaleLowerCase().slice(0, 1)
                      }`}
                  />
                </p>
              </div>
            </section>
            <section className='grid grid-cols-1 gap-5 mt-1'>
              <div>
                <label htmlFor={language.startsWith('E') ? "description" : `description_${language.toLocaleLowerCase().slice(0, 1)
                  }`}
                  className='text-xs'
                >
                  Course Description
                  {language.startsWith('E') ? "" : ` (${language})`}
                  <span className='text-lg text-red-500'>*</span>
                </label>
                <Field
                  id={language.startsWith('E') ? "description" : `description_${language.toLocaleLowerCase().slice(0, 1)
                    }`}
                  className="input-style pt-5 pb-5 bg-primaryGray"
                  name={language.startsWith('E') ? "description" : `description_${language.toLocaleLowerCase().slice(0, 1)
                    }`}
                  placeholder='Type course description here...'
                  as='textarea'
                />
                <p className='mt-2 text-red-500 text-sm'>
                  <ErrorMessage
                    name={language.startsWith('E') ? "description" : `description_${language.toLocaleLowerCase().slice(0, 1)
                      }`}
                  />
                </p>
              </div>
            </section>
            <section className='grid grid-cols-1 gap-5 mt-3 mb-5'>
              <div>
                <label className='text-xs'>
                  Course Content File {language.startsWith('E') ? "" : `(${language})`}
                </label>
                <input
                  type="file" className='hidden' id={`course-file-${symbol}`}
                  onChange={(e) => uploadCourseFile(e, symbol)}
                />
                <div className='w-[100%] flex-col flex items-center justify-center mt-2 border-2 border-dotted border-slate-300 h-[100px] rounded-lg bg-primaryGray'>
                  <label htmlFor={`course-file-${symbol}`} className='btn bg-primary-blue cursor-pointer px-4 py-2 text-sm'>
                    Upload File
                  </label>
                  <p className='text-xs text-center px-8 pt-2 text-slate-500'>
                    Upload course content for {language}.
                  </p>
                </div>
                {currentFilePath &&
                  <div className='border-2 w-[100%] rounded-lg p-3 mt-3 border-dotted border-slate-300 bg-primaryGray flex justify-between items-center'>
                    <span className='text-sm font-semibold text-primary-blue break-all underline'>
                      {currentFilePath.split('/').pop() || 'Uploaded File'}
                    </span>
                    <i className='bi bi-trash text-red-500 text-lg cursor-pointer' onClick={() => {
                      if (symbol === 'en') setFilePathEn('')
                      if (symbol === 'fr') setFilePathFr('')
                      if (symbol === 'pt') setFilePathPt('')
                    }} />
                  </div>
                }
              </div>
            </section>
          </>
        )
      }
    }
  )

  return (
    <>  {isUploading && <ResourceLoader />}
      <header className='flex justify-between'>
        <h3 className='text-md font-semibold'>
          Create New Course
        </h3>
        <div className='flex items-center'>
          <Link to={'/admin/course/'} className='btn-light pl-8 pr-8 bg-slate-300'>
            Back
          </Link>
          <button className='btn ml-5 bg-primary-blue'
            disabled={courseLoading}
            onClick={() => {
              const submitBtn = document.querySelector('#create-course-btn') as HTMLButtonElement
              submitBtn.click()
            }}
          >
            {courseLoading ? 'Loading...' : 'Save & Continue'}
          </button>
        </div>
      </header>
      <section className='border flex justify-between pr-0 px-7 border-slate-400 rounded-lg mx-3 mt-8'>
        <div className='w-[40%] py-7'>
          <div>
            <h5 className='text-sm'>
              Upload Course Media
            </h5>
            <input
              type="file" className='hidden' id='course-image'
              onChange={uploadCourseImage} accept="image/*"
            />
            <div className='w-[90%] flex-col flex items-center justify-center mt-3 mx-auto border-2 border-dotted border-slate-300 h-[200px] rounded-lg'>
              <label htmlFor="course-image" className='btn bg-primary-blue'>
                Upload Image
              </label>
              <p className='text-xs text-center px-8 pt-4'>
                Select clear image of your course.
                Please note that GIF files will not animate.
              </p>
            </div>
            {logo &&
              <div className='border-2 w-[90%] mx-auto rounded-lg p-5 mt-3 border-dotted border-slate-300'>
                <h5 className='flex justify-end '>
                  <i className='bi bi-trash text-red-500 text-sm mr-1' />
                </h5>
                <img src={logo} alt="course image" className='w-[200px h-[100px] block mx-auto' />
              </div>
            }
          </div>
          <div className="mt-8">
            <h5 className='text-sm'>
              Upload Course Certificate (Optional)
            </h5>
            <input
              type="file" className='hidden' id='course-certificate'
              onChange={uploadCertificate} accept="application/pdf,image/*"
            />
            <div className='w-[90%] flex-col flex items-center justify-center mt-3 mx-auto border-2 border-dotted border-slate-300 h-[200px] rounded-lg'>
              <label htmlFor="course-certificate" className='btn bg-primary-blue cursor-pointer'>
                Upload Certificate
              </label>
              <p className='text-xs text-center px-8 pt-4'>
                Select a certificate file for this course (PDF or Image).
              </p>
            </div>
            {certificate &&
              <div className='border-2 w-[90%] mx-auto rounded-lg p-5 mt-3 border-dotted border-slate-300'>
                <h5 className='flex justify-end '>
                  <i className='bi bi-trash text-red-500 text-sm mr-1 cursor-pointer' onClick={() => setCertificate('')} />
                </h5>
                {certificate.endsWith('.pdf') ? (
                  <div className='flex items-center justify-center h-[100px]'>
                    <span className='ml-2 text-sm font-semibold text-primary-blue underline'>Certificate Uploaded</span>
                  </div>
                ) : (
                  <img src={certificate} alt="course certificate" className='w-[200px] h-[100px] block mx-auto object-contain' />
                )}
              </div>
            }
          </div>
        </div>
        <div className='rounded-e-lg w-[58%] bg-white p-5 px-8'>
          <h5 className='text-lg font-semibold'>
            Course Information
          </h5>
          <p className='text-xs font-semibold border-b border-gray-300 pb-2 mt-1 mb-5'>
            Give detailed Information about the course
          </p>
          <div className="mb-3">
            <label className="block font-semibold w-full text-sm mb-1">
              Would you like to add course details in other languages?
            </label>
            <div className='flex items-center'>
              <input
                type="checkbox"
                className="mr-2"
                checked={useMultipleLanguages}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUseMultipleLanguages(e.target.checked)}
              />
              <label className="text-xs">
                Yes, I want to add multiple languages
              </label>
            </div>
          </div>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize={true}
            onSubmit={createCourse}
          >
            {({ values, setFieldError, setFieldValue, setFieldTouched, errors }) => (
              <Form>
                {useMultipleLanguages ? (
                  <HorizontalTab withPadding={false} tabsData={tabsData} />
                ) : <HorizontalTab withPadding={false} tabsData={tabsData.slice(0, 1)} />
                }
                <section className='grid grid-cols-1 gap-5 mt-1'>
                  <div>
                    <label htmlFor="category" className='text-xs'>
                      Course Category <span className='text-lg text-red-500'>*</span>
                    </label>
                    <CustomDropdown
                      options={
                        categories?.map((category: any) => ({
                          label: category?.name,
                          value: category?.id,
                        })) || []
                      }
                      value={{
                        label:
                          categories?.find(
                            (category: any) => category?.id === values.category
                          )?.name || "",
                        value: values.category,
                      }}
                      onChange={(value: any) => {
                        setFieldValue("category", value?.value);
                        setFieldError("category", "");
                      }}
                      onBlur={() => {
                        setFieldTouched("category", true, true);
                      }}
                      name="category"
                      className="bg-primaryGray select-custom"
                      width="w-full"
                      placeholder="Select Category"
                    />
                    {errors.category &&
                      <p className='mt-2 text-red-500 text-sm'>
                        <ErrorMessage name='category' />
                      </p>}
                  </div>
                </section>
                {/* <div>
                        <label
                         htmlFor={'rating'}
                         className='text-xs'
                        >  Rating
                           <span className='text-lg text-red-500'>*</span>
                        </label>
                        <Field 
                            id={'rating'}
                            type={'number'}
                            className="input-style bg-primaryGray" 
                            name={'rating'}
                            placeholder='Type course rating here...'
                        />
                        <p className='mt-2 text-red-500 text-sm'>
                            <ErrorMessage 
                                name={'rating'} 
                            />
                        </p>
                    </div> 
                         <section className='grid grid-cols-1 gap-5 mt-1'>
                            <div>
                                <label htmlFor="code" className='text-xs'>
                                  Course Code <span className='text-lg text-red-500'>*</span>
                                </label>
                                <CustomDropdown
                                  options={ creditsCodes?.map( (code: any) => {
                                      return { label: code?.creditCode, value: code?.id  }
                                    })
                                  }
                                  value={
                                    { 
                                       label: creditsCodes?.find( (code:any) => {
                                           return code?.id === values?.code
                                       })?.creditCode, 
                                       value: values.code
                                    }
                                  } 
                                  onChange={ (value:any) => {
                                    setFieldValue('code', value?.value)
                                  }}
                                  onBlur={ (value: any) => {
                                          setFieldTouched('code', true, true)
                                  }}
                                  name={'code'}
                                  className={'bg-primaryGray select-custom'}
                                  width={'w-full'}
                                  placeholder={'Select code'}
                                /> 
                              { errors.code &&                               
                                <p className='mt-2 text-red-500 text-sm'>
                                    <ErrorMessage name='code' />
                                </p>}
                            </div>
                         </section> */}
                <div className='flex mt-3 items-center justify-between pr-3'>
                  <span className='text-sm'>
                    Status (published or draft)
                  </span>
                  {/* @ts-ignore */}
                  <Switch
                    height={20}
                    width={40}
                    handleDiameter={18}
                    checked={values.status === 'published'}
                    onChange={(): void => {
                      setFieldValue('status', values.status === 'published' ? 'draft' : 'published')
                    }}
                  />
                </div>
                <button className='hidden' id='create-course-btn' type='submit' />
              </Form>)}
          </Formik>
        </div>
      </section>
    </>
  )
}

export default CourseInformation